import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useRequest } from 'domain/core/hooks/useRequest';
import { ApiMethodsEnum } from 'infrastructure/enums/ApiMethodsEnum';
import { storageService } from 'infrastructure/services/StorageService';
import { useContextSelector as useCS } from 'use-context-selector';
import { TUpdate } from 'domain/types';
import {
  CreateJobPostConfigContext,
  TCreateJobPostContextState,
} from 'domain/createJobPostConfig/CreateJobPostContext';
import { createJobPostConfigSelectors } from 'domain/createJobPostConfig/selectors';
import { useStateMachine } from 'little-state-machine';
import { showSystemToast } from 'view/components/SystemToast';
import { resetForm } from 'view/pages/createJobPost/helpers/resetForm';

export const useCreateJobPost = (): any => {
  const navigate = useNavigate();

  const update: TUpdate<TCreateJobPostContextState> | undefined = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.update
  );

  const { actions } = useStateMachine({
    actions: { resetForm },
  });

  const { data, isLoading, status, error, send } = useRequest({
    url: '/api/v1/jobPosts',
    method: ApiMethodsEnum.Post,
    sendOnStart: false,
    body: {},
  });

  const sendFunc = async (body = {}) => {
    if (update) update({ createJobPostData: data, screeningQuestions: data?.faq || [] });
    send(body).then();
  };

  // A failed create used to bail out silently, leaving the button looking dead — surface it.
  useEffect(() => {
    if (isLoading || !error) return;
    showSystemToast(
      'error',
      'Error',
      error?.response?.data?.message || 'Failed to create job post. Please try again.'
    );
  }, [error, isLoading]);

  useEffect(() => {
    // Creation answers 201, but accept 200 too so a backend that returns it still moves on.
    if (isLoading || !data || error || (status !== 201 && status !== 200)) return;
    if (update) update({ createJobPostData: data, screeningQuestions: data?.faq || [] });
    storageService.setItem('createJobPostId', data?.id).then();
    actions.resetForm();
    navigate('/create-job-post/step1', { replace: true });
  }, [data, isLoading, error, status, update]);

  return {
    isLoading,
    createJobPost: sendFunc,
  };
};
